










































































































import Vue from 'vue';
import { FETCH_DUNNING_CAMPAIGN_BLUEPRINT } from '@/store/action-types';
import { SET_CURRENT_DUNNING_EMAIL_BLUEPRINT_ID } from '@/store/mutation-types';
import { LOAD_STATE } from '@/store/shared';
import EmailPreview from '@/components/UI/EmailPreview.vue';
import CampaignFilter from '@/components/Dunning/Analytics/DunningCampaignFilter.vue';
import { ICampaignBlueprint, IEmailBlueprint, IEmailCount, IEmail, IState, LoadState } from '@/store/types';
import DashLoader from '@/components/UI/Loader/DashLoader.vue';
import { ascending } from 'vega';
import { DUNNING_CAMPAIGN, EMAIL } from '@/helpers/stats-utils';
import { getDateEndpoints } from '@/helpers/date-utils';
import DatePickerCondensed from '@/components/UI/DatePickerCondensed.vue';
import { timeDay } from 'd3-time';
import statsService from '@/api/stats.service';

interface IEmailStat {
  scheduled: number;
  sent: number;
  opened: number;
  clicked: number;
  delivered: number;
  recovered: number;
  emailBlueprintGuid: string;
}

export default Vue.extend({
  name: 'CampaignBlueprintCard',
  components: {
    DashLoader,
    EmailPreview,
    CampaignFilter,
    DatePickerCondensed,
  },
  data() {
    const defaultStartDate = timeDay.offset(new Date(), -30);
    const defaultEndDate = timeDay.offset(new Date(), 2);
    const [startDate, endDate] = getDateEndpoints([defaultStartDate, defaultEndDate]);
    return {
      startDate,
      endDate,
      loading: true,
      previewEmail: false,
      emailToPreview: null,
      campaignFilters: [DUNNING_CAMPAIGN.BLUEPRINT],
      selectedCampaignBlueprintId: undefined as string | undefined,
      statsLoadState: LOAD_STATE.UNLOADED as LoadState,
      dunningEmailCounts: [] as IEmailCount[],
    };
  },
  async created() {
    this.selectedCampaignBlueprintId = this.primaryCampaignBlueprintId;
    this.fetchData();
    this.loading = false;
  },
  computed: {
    state(): IState {
      return this.$store.state;
    },
    primaryCampaignBlueprintId(): string | undefined {
      return this.$store.getters.primaryCampaignBlueprintId;
    },
    preselected(): string[] {
      return this.selectedCampaignBlueprintId ? [`${DUNNING_CAMPAIGN.BLUEPRINT}.${this.selectedCampaignBlueprintId}`] : [];
    },
    campaignBlueprints(): ICampaignBlueprint[] {
      const campaignBlueprints = Object.values(this.state.campaignBlueprints);
      // if campaignBlueprint doesn't have an audience, move it to the start of the list
      return campaignBlueprints.sort((a, b) => {
        if (a.audience && !b.audience) {
          return 1;
        }
        if (!a.audience && b.audience) {
          return -1;
        }
        return 0;
      });
    },
    selectedCampaign(): ICampaignBlueprint | undefined {
      if (this.selectedCampaignBlueprintId) {
        return this.state.campaignBlueprints[this.selectedCampaignBlueprintId];
      }
      return undefined;
    },
    statsLoading(): boolean {
      return this.statsLoadState === LOAD_STATE.LOADING || this.statsLoadState === LOAD_STATE.UNLOADED;
    },
    emails(): IEmailBlueprint[] {
      return this.selectedCampaign?.emails || [];
    },
    emailsWithStats(): IEmailBlueprint[] {
      return this.emails
        .map((email) => {
          const emailStat = this.statMap[email.guid];
          if (emailStat) {
            const openRate = emailStat.opened / emailStat.sent;
            const clickRate = emailStat.clicked / emailStat.sent;
            return {
              ...email,
              ...emailStat,
              openRate: openRate ? `${(openRate * 100).toFixed(1)}%` : '-',
              clickRate: clickRate ? `${(clickRate * 100).toFixed(1)}%` : '-',
            };
          }
          return email;
        })
        .sort((a: IEmailBlueprint, b: IEmailBlueprint) => ascending(a.sendOnDay, b.sendOnDay));
    },
    campaignStats(): IEmailStat[] {
      // map emailBlueprintIds to email stats
      return this.emails.map((email) => {
        const emailStats = this.dunningEmailCounts.filter((emailCount) => emailCount.emailBlueprintId === email._id);
        const totals = emailStats.reduce(
          (acc, emailCount) => {
            if (emailCount.status === 'scheduled') {
              acc.scheduled += emailCount.count;
            } else if (emailCount.status === 'sent') {
              acc.sent += emailCount.count;
            } else if (emailCount.status === 'delivered') {
              acc.sent += emailCount.count;
              acc.delivered += emailCount.count;
            } else if (emailCount.status === 'opened') {
              acc.sent += emailCount.count;
              acc.delivered += emailCount.count;
              acc.opened += emailCount.count;
            } else if (emailCount.status === 'clicked') {
              acc.sent += emailCount.count;
              acc.opened += emailCount.count;
              acc.delivered += emailCount.count;
              acc.clicked += emailCount.count;
            } else if (emailCount.status === 'recovered') {
              acc.sent += emailCount.count;
              acc.opened += emailCount.count;
              acc.delivered += emailCount.count;
              acc.clicked += emailCount.count;
              acc.recovered += emailCount.count;
            }
            return acc;
          },
          { scheduled: 0, sent: 0, opened: 0, clicked: 0, delivered: 0, recovered: 0 }
        );
        return {
          ...totals,
          emailBlueprintGuid: email.guid as string,
        };
      });
    },
    statMap(): Record<string, IEmailStat> {
      return this.campaignStats.reduce(
        (acc, stat) => {
          acc[stat.emailBlueprintGuid] = stat;
          return acc;
        },
        {} as Record<string, IEmailStat>
      );
    },
  },
  methods: {
    editEmail(email: IEmailBlueprint) {
      if (this.selectedCampaignBlueprintId) {
        this.$store.commit(SET_CURRENT_DUNNING_EMAIL_BLUEPRINT_ID, email.guid);
        this.$router.push({ name: 'dunningcampaign', params: { id: this.selectedCampaignBlueprintId } });
      }
    },
    showPreviewEmail(email: any) {
      this.previewEmail = !this.previewEmail;
      if (this.previewEmail) {
        this.emailToPreview = email;
      }
    },
    onFilterUpdate(newFilter: { campaignBlueprintId?: string[] }) {
      if (newFilter.campaignBlueprintId && newFilter.campaignBlueprintId.length) {
        this.selectedCampaignBlueprintId = newFilter.campaignBlueprintId[0];
      }
    },
    handleDateChange(value: Date[]) {
      this.startDate = value[0];
      this.endDate = value[1];
      this.fetchData();
    },
    async fetchData() {
      this.statsLoadState = LOAD_STATE.LOADING;
      try {
        const emailCountData = await statsService.dunningEmailCounts({
          breakdown: [EMAIL.BLUEPRINT, EMAIL.STATUS],
          filter: {
            startDate: this.startDate,
            endDate: this.endDate,
          },
        });
        this.dunningEmailCounts = emailCountData;
        this.statsLoadState = LOAD_STATE.LOADED;
      } catch (e) {
        console.error(e);
        this.statsLoadState = LOAD_STATE.ERROR_LOADING;
      }
    },
  },
  watch: {
    selectedCampaignBlueprintId(newValue: string, oldValue: string) {
      if (newValue && !this.$store.state.campaignBlueprints[newValue]) {
        this.$store.dispatch(FETCH_DUNNING_CAMPAIGN_BLUEPRINT, newValue);
      }
    },
  },
});
